import React from 'react';
import { useSelector } from 'react-redux';
import { Box } from '../../../../../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../../../../../CommonComponents/OPL/Text/Text';
import { Badge } from '../../../../../../../../../../CommonComponents/OPL/Badge/Badge';
import { Button } from '../../../../../../../../../../CommonComponents/OPL/Button/Button';
import { Category, CollapsableHeadersData } from '../../FlowTable.meta';
import {
  Menu,
  MenuContent,
  MenuHeader,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from '../../../../../../../../../../CommonComponents/OPL/Menu/Menu';
import { Checkbox } from '../../../../../../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { useFpId } from '../../../../../../../../../hooks/useFpId';
import { useClientId } from '../../../../../../../../../../_hooks/useClientId';
import { urlWithParams } from '../../../../../../../../../../CommonUtils/urlWithParams';
import { isAdviserArea as selectIsAdviserArea } from '../../../../../../../../../../CommonSelectors/CommonAreaSelector';
import { TableAdditionalDetailsDTO } from '../../../../api/useEstatePlanningFlowQuery';

interface Props {
  categories: Category[];
  hasHeaderSelected: (id: number) => boolean;
  toggleHeader: (id: number) => void;
  collapsableHeadersData: CollapsableHeadersData;
  tableAdditionalDetails: TableAdditionalDetailsDTO;
  isSpouse: boolean;
}

export const TableActions = ({
  categories,
  hasHeaderSelected,
  toggleHeader,
  collapsableHeadersData,
  tableAdditionalDetails,
  isSpouse,
}: Props) => {
  const fpId = useFpId();
  const clientId = useClientId();

  const isAdviserArea = useSelector(selectIsAdviserArea);

  const isPhase2 = false;

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        mt="56"
        px="24"
        py="16"
        borderTop="1px solid"
        borderBottom="1px solid"
        borderColor="neutral.200"
      >
        <Box display="flex" alignItems="center">
          <Text fontSize="h6" fontWeight={600} variant="tabular" ml="8">
            {tableAdditionalDetails.resourcesAtPassingText}
          </Text>

          <Badge variant="tertiary" ml="8">
            {tableAdditionalDetails.ageAtPassingText}
          </Badge>
        </Box>

        <Box display="flex" alignItems="center" gridGap="8">
          {isAdviserArea && (
            <Button
              variant="subtleInactive"
              leftIcon="far fa-file-export"
              onClick={async () => {
                window.open(
                  urlWithParams('/Consumer/EstatePlanningFlow/DownloadCsv', {
                    clientId,
                    fpId,
                    isSpouse,
                  }),
                  '_blank',
                  'noopener noreferrer',
                );
              }}
            >
              Export as CSV
            </Button>
          )}

          {isPhase2 && (
            <Menu>
              <MenuTrigger>
                <Button variant="subtleInactive" leftIcon="far fa-columns">
                  Column
                </Button>
              </MenuTrigger>

              <MenuContent>
                {categories.map(({ name, id, headers }, index) => {
                  const isLast = index + 1 === categories.length;

                  if (id === 0) {
                    return null;
                  }

                  return (
                    <React.Fragment key={id}>
                      <MenuHeader>{name}</MenuHeader>
                      {headers.map(header => {
                        const hasCollapsedHeader =
                          Object.keys(collapsableHeadersData).length !== 0 &&
                          Object.keys(collapsableHeadersData.header).includes(header.id.toString());

                        if (
                          hasCollapsedHeader &&
                          header.name !==
                            collapsableHeadersData.header[header.id].collapsedData.name
                        ) {
                          return null;
                        }

                        return (
                          <MenuItem
                            key={header.id}
                            onSelect={() => {
                              toggleHeader(header.id);
                            }}
                          >
                            <Checkbox checked={!hasHeaderSelected(header.id)}>
                              {header.name}
                            </Checkbox>
                          </MenuItem>
                        );
                      })}

                      {!isLast && <MenuSeparator />}
                    </React.Fragment>
                  );
                })}
              </MenuContent>
            </Menu>
          )}
        </Box>
      </Box>
    </Box>
  );
};
